import {
  Background,
  ControlButton,
  Controls,
  type Edge,
  Handle,
  type Node,
  PanOnScrollMode,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { memo } from 'react';

import { PartialAsset } from '@/hooks/useAssets';
import { RiskSummary } from '@/sections/Assets';
import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';
import { getDisplayIdentifierWithFallback } from '@/utils/displayIdentifier.util';
import { createVerticalContainer } from '@/utils/reactFlor.util';
import { useComponentDidUpdate } from '@/utils/reactHooks.util';

function ReactNodeElComponent({
  data,
}: {
  data: {
    children: ReactNode;
    className?: string;
    showHandles?: boolean;
    nodeStyle?: boolean;
  };
}) {
  return (
    <div
      className={cn(
        data.nodeStyle &&
          'border-chariot-stroke h-full rounded border-2 bg-transparent',
        data.className
      )}
    >
      {data.showHandles && (
        <Handle
          className="bg-chariot-stroke ml-[1.5px] size-3"
          type="target"
          position={Position.Left}
        />
      )}
      {data.children}
      {data.showHandles && (
        <Handle
          className="bg-chariot-stroke mr-[1.5px] size-3"
          type="source"
          position={Position.Right}
        />
      )}
    </div>
  );
}

function AssetNodeComponent({
  data,
}: {
  data: { asset: PartialAsset; className?: string; isSelected?: boolean };
}) {
  const { drawerState } = useGlobalState();

  return (
    <div
      className={cn(
        'group/node flex cursor-pointer justify-start overflow-hidden rounded bg-transparent px-2 py-1',
        data.isSelected
          ? 'border-chariot-brand-primary border-[3px]'
          : 'border-chariot-stroke border-2',
        data.className
      )}
      onClick={() => {
        drawerState.handleOpenAssetDrawer(data.asset as any); // TODO: Fix typing for mixed entity types
      }}
    >
      <Handle
        className={cn(
          'bg-chariot-stroke ml-[1.5px] size-3',
          data.isSelected && 'bg-chariot-brand-primary'
        )}
        type="target"
        position={Position.Left}
      />

      <div className="flex flex-col overflow-hidden">
        <div className="text-chariot-brand-primary flex w-full items-center gap-1 truncate font-medium group-hover/node:underline">
          <RiskSummary riskSummary={data.asset?.riskSummary} />
          {data.asset && getDisplayIdentifierWithFallback(data.asset, '')}
        </div>
        <div className="w-full truncate">{data.asset?.group}</div>
      </div>
      <Handle
        className={cn(
          'bg-chariot-stroke mr-[1.5px] size-3',
          data.isSelected && 'bg-chariot-brand-primary'
        )}
        type="source"
        position={Position.Right}
      />
    </div>
  );
}

const nodeTypes = {
  react: memo(ReactNodeElComponent),
  asset: memo(AssetNodeComponent),
};
const dimensions = {
  nodeWidth: 350,
  nodeHeight: 60,
  nodeMarginBottom: 20,

  moreNodeHeight: 40,
  labelMarginBottom: 10,

  xGapBetweenNodes: 50,

  containerPaddingX: 20,
  containerLabelHeight: 20,
};
const defaultNodeShowCount = 3;

interface AssetGraphProps {
  child: PartialAsset[];
  parent: PartialAsset[];
  asset: PartialAsset;
}
export function AssetsGraph(props: AssetGraphProps) {
  const { fitView } = useReactFlow();
  const [parentNMore, setParentNMore] = useState(0);
  const [childNMore, setChildNMore] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const getNodeAndLinks = useCallback((): { nodes: Node[]; edges: Edge[] } => {
    const assetNode: Node = {
      id: props.asset.key,
      type: 'asset',
      position: {
        x: 0,
        y: 0,
      },
      data: {
        asset: props.asset,
        isSelected: true,
      },
      connectable: false,
      targetPosition: Position.Left,
      sourcePosition: Position.Right,
      width: dimensions.nodeWidth,
      height: dimensions.nodeHeight,
      selectable: true,
    };

    const nodes: Node[] = [assetNode];
    const links: Edge[] = [];

    const showNChild = defaultNodeShowCount + childNMore;
    const showNParent = defaultNodeShowCount + parentNMore;

    if (props.parent.length > 0) {
      const parentNodes: Node[] = [];

      parentNodes.push({
        id: 'parentAssetsLabel',
        type: 'react',
        draggable: false,
        selectable: false,
        connectable: false,
        data: {
          children: (
            <div className="text-chariot-text-secondary flex w-full justify-center text-sm font-semibold">
              Parent Assets
            </div>
          ),
        },
        position: {
          x: 0,
          y: 0,
        },
        height: dimensions.containerLabelHeight,
        width: dimensions.nodeWidth,
        style: {
          display: 'flex',
          justifyContent: 'center',
          marginBottom: dimensions.labelMarginBottom,
          zIndex: 10,
        },
      });

      parentNodes.push(
        ...props.parent
          .slice(0, showNParent)
          .filter(asset => asset && asset.key && asset.key !== props.asset.key) // Extra safety check
          .map((asset): Node => {
            return {
              id: asset.key,
              position: {
                x: 0,
                y: 0,
              },
              type: 'asset',
              data: { asset },
              targetPosition: Position.Left,
              sourcePosition: Position.Right,
              height: dimensions.nodeHeight,
              width: dimensions.nodeWidth,
              selectable: true,
              style: {
                marginBottom: dimensions.nodeMarginBottom,
              },
            };
          })
      );

      if (props.parent.length > showNParent) {
        parentNodes.push({
          id: 'more-parent',
          position: {
            x: 0,
            y: 0,
          },
          type: 'react',
          data: {
            children: (
              <div
                className="flex size-full items-center justify-center"
                onClick={() => {
                  setParentNMore(prevNMore => prevNMore + 100);
                }}
              >{`+${props.parent.length - showNParent} more`}</div>
            ),
            showHandles: true,
            nodeStyle: true,
          },
          targetPosition: Position.Left,
          sourcePosition: Position.Right,
          height: dimensions.moreNodeHeight,
          width: dimensions.nodeWidth,
          selectable: true,
          style: {
            marginBottom: dimensions.nodeMarginBottom,
          },
        });
      }

      nodes.push(
        ...createVerticalContainer({
          nodes: parentNodes,
          containerPaddingX: dimensions.containerPaddingX,
          containerMargin: dimensions.xGapBetweenNodes,
          parentId: 'parentGroup',
          direction: 'left',
        })
      );

      links.push(
        ...parentNodes
          .filter(
            node =>
              node.id !== 'parentAssetsLabel' &&
              node.id !== 'more-parent' &&
              node.id !== assetNode.id &&
              node.type === 'asset'
          ) // Only connect actual asset nodes, exclude labels and self
          .map((node, index): Edge => {
            // Add index to ensure unique edge IDs when multiple relationships exist between same nodes
            return {
              id: `${node.id}-${assetNode.id}-parent-${index}`,
              source: node.id,
              target: assetNode.id,
              type: 'smoothstep',
              animated: true,
              className: '[&_path]:stroke-chariot-stroke [&_path]:stroke-2',
              style: {
                strokeDasharray: '5,5', // Add dashed line style
              },
            };
          })
      );
    }

    if (props.child.length > 0) {
      const childNodes: Node[] = [];

      childNodes.push({
        id: 'childAssetsLabel',
        type: 'react',
        draggable: false,
        selectable: false,
        connectable: false,
        data: {
          children: (
            <div className="text-chariot-text-secondary flex w-full justify-center text-sm font-semibold">
              Child Assets
            </div>
          ),
        },
        position: {
          x: 0,
          y: 0,
        },
        height: dimensions.containerLabelHeight,
        width: dimensions.nodeWidth,
        style: {
          display: 'flex',
          justifyContent: 'center',
          marginBottom: dimensions.labelMarginBottom,
          zIndex: 10,
        },
      });

      childNodes.push(
        ...props.child
          .slice(0, showNChild)
          .filter(asset => asset && asset.key && asset.key !== props.asset.key) // Extra safety check
          .map((asset): Node => {
            return {
              id: asset.key,
              position: {
                x: 0,
                y: 0,
              },
              type: 'asset',
              data: { asset },
              targetPosition: Position.Left,
              sourcePosition: Position.Right,
              height: dimensions.nodeHeight,
              width: dimensions.nodeWidth,
              selectable: true,
              style: {
                marginBottom: dimensions.nodeMarginBottom,
              },
            };
          })
      );

      if (props.child.length > showNChild) {
        childNodes.push({
          id: 'more-child',
          position: {
            x: 0,
            y: 0,
          },
          type: 'react',
          data: {
            children: (
              <div
                className="flex size-full items-center justify-center"
                onClick={() => {
                  setChildNMore(prevNMore => prevNMore + 100);
                }}
              >{`+${props.child.length - showNChild} more`}</div>
            ),
            showHandles: true,
            nodeStyle: true,
          },
          targetPosition: Position.Left,
          sourcePosition: Position.Right,
          height: dimensions.moreNodeHeight,
          width: dimensions.nodeWidth,
          selectable: true,
          style: {
            marginBottom: dimensions.nodeMarginBottom,
          },
        });
      }

      nodes.push(
        ...createVerticalContainer({
          nodes: childNodes,
          containerPaddingX: dimensions.containerPaddingX,
          containerMargin: dimensions.xGapBetweenNodes,
          parentId: 'childGroup',
          direction: 'right',
        })
      );

      links.push(
        ...childNodes
          .filter(
            node =>
              node.id !== 'childAssetsLabel' &&
              node.id !== 'more-child' &&
              node.id !== assetNode.id &&
              node.type === 'asset'
          ) // Only connect actual asset nodes, exclude labels and self
          .map((node, index): Edge => {
            // Add index to ensure unique edge IDs when multiple relationships exist between same nodes
            return {
              id: `${assetNode.id}-${node.id}-child-${index}`,
              source: assetNode.id,
              target: node.id,
              type: 'smoothstep',
              animated: true,
              className: '[&_path]:stroke-chariot-stroke [&_path]:stroke-2',
              style: {
                strokeDasharray: '5,5', // Add dashed line style
              },
            };
          })
      );
    }

    return {
      nodes: nodes,
      edges: links,
    };
  }, [
    JSON.stringify({
      parent: props.parent,
      child: props.child,
      asset: props.asset,
    }),
    parentNMore,
    childNMore,
  ]);

  const { nodes: initialNodes, edges: initialEdges } = getNodeAndLinks();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);

  function reCenter() {
    fitView({
      duration: 400,
      padding: 0.3,
    });
  }

  useComponentDidUpdate(() => {
    const { nodes, edges } = getNodeAndLinks();

    setNodes(nodes);
    setEdges(edges);

    setTimeout(() => {
      window.requestAnimationFrame(() => {
        reCenter();
      });
    }, 100);
  }, [getNodeAndLinks]);

  useEffect(() => {
    const { nodes, edges } = getNodeAndLinks();

    setNodes(nodes);
    setEdges(edges);

    // Wait for next frame to ensure nodes are properly laid out
    window.requestAnimationFrame(() => {
      fitView({
        duration: 0,
        padding: 0.3,
      });
      setIsReady(true);
    });
  }, []);

  if (!isReady) {
    return null; // or return a loading spinner if preferred
  }

  return (
    <div className="relative size-full overflow-hidden">
      <ReactFlow
        id="asset"
        nodes={nodes}
        edges={edges}
        panOnScroll
        fitView
        elementsSelectable={false}
        nodesConnectable={false}
        nodesDraggable={false}
        zoomOnScroll={false}
        panOnScrollMode={PanOnScrollMode.Vertical}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        maxZoom={2}
        minZoom={0.4}
        className={cn('size-full bg-transparent', !isReady && 'opacity-0')}
      >
        <Controls
          fitViewOptions={{
            duration: 400,
            padding: 0.3,
          }}
          showInteractive={false}
          className="border-chariot-stroke bg-chariot-background-primary/90 [&_button]:border-b-chariot-stroke [&_button]:text-chariot-text-primary hover:[&_button]:bg-chariot-background-secondary/60 rounded-lg border-2 backdrop-blur-sm [&_button]:border-b-2 [&_button]:bg-transparent [&_button]:transition-all [&_button]:duration-200 last:[&_button]:border-b-0"
        >
          <ControlButton onClick={reCenter}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="30"
              viewBox="0 0 32 30"
              fill="none"
            >
              {/* This is the fit view button */}
              <g clipPath="url(#clip0_2_2)">
                <path
                  d="M3.692 4.63005C3.692 4.10005 4.092 3.69205 4.631 3.69205H9.846V5.23661e-05H4.708C2.13 5.23661e-05 0 2.05405 0 4.63005V9.84605H3.692V4.63005ZM27.354 5.23661e-05H22.154V3.69205H27.324C27.854 3.69205 28.308 4.09205 28.308 4.63105V9.84605H32V4.63105C32.0009 4.02138 31.8813 3.41753 31.6479 2.85428C31.4146 2.29102 31.0722 1.77947 30.6404 1.34906C30.2086 0.918646 29.6959 0.577878 29.1319 0.346362C28.5679 0.114845 27.9637 -0.00284906 27.354 5.23661e-05ZM28.308 24.8301C28.308 25.3621 27.908 25.7701 27.369 25.7701H22.154V29.5381H27.369C29.946 29.5381 32 27.4081 32 24.8311V19.6921H28.308V24.8301ZM4.631 25.7701C4.1 25.7701 3.692 25.3701 3.692 24.8301V19.6921H0V24.8311C0 27.4081 2.13 29.5381 4.708 29.5381H9.846V25.7701H4.631Z"
                  fill="currentColor"
                />
                <circle cx="16" cy="15" r="5" fill="currentcolor" />
              </g>
              <defs>
                <clipPath id="clip0_2_2">
                  <rect width="32" height="30" fill="currentcolor" />
                </clipPath>
              </defs>
            </svg>
          </ControlButton>
        </Controls>
        <Background color="rgb(var(--chariot-stroke) / 0.3)" size={4} />
      </ReactFlow>
    </div>
  );
}
